import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;

function GraphicPreviewPhotoFrame({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location, backgroundPhoto } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const accent = accentBlockColor || teamColors.primary;

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(90deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const badgeFontSize = Math.max(28, Math.min(Math.round(heroFontSize / 4), 56));

  const homeNameFontSize = (homeTeam || 'YOUR TEAM').length > 14 ? 52 : (homeTeam || 'YOUR TEAM').length > 9 ? 64 : 80;
  const awayNameFontSize = (awayTeam || 'OPPONENT').length > 14 ? 52 : (awayTeam || 'OPPONENT').length > 9 ? 64 : 80;

  const logoCircleStyle = {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    marginBottom: 16,
  };

  return (
    <div
      id="graphic-preview"
      style={{
        width: W,
        height: H,
        position: 'relative',
        overflow: 'hidden',
        background: getBackground(),
        borderRadius: 16,
        fontFamily: 'Barlow, sans-serif',
      }}
    >
      {/* Background Photo Layer */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: W,
          height: H,
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        {backgroundPhoto ? (
          <img
            src={backgroundPhoto}
            alt="Background"
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            <div style={{ fontSize: 120, marginBottom: 24 }}>📸</div>
            <div style={{ fontSize: 32, fontWeight: 600 }}>Upload a team or field photo</div>
          </div>
        )}
      </div>

      {/* Dark Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 100%)',
          zIndex: 1,
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '60px 60px 0 60px',
        }}
      >
        {/* Top — GAME DAY badge */}
        <div>
          <div
            style={{
              display: 'inline-block',
              background: accent,
              color: '#ffffff',
              fontFamily: heroFont,
              fontSize: badgeFontSize,
              fontWeight: 700,
              letterSpacing: 4,
              padding: '14px 40px',
              borderRadius: 40,
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}
          >
            GAME DAY
          </div>
        </div>

        {/* Bottom — Teams + Details */}
        <div>
          {/* Teams Row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              marginBottom: 40,
              gap: 24,
            }}
          >
            {/* Home Team */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flex: 1,
              }}
            >
              <div style={logoCircleStyle}>
                {homeTeamLogo ? (
                  <img
                    src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                    alt={homeTeam}
                    style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
                  />
                ) : (
                  <span style={{ fontSize: 60 }}>
                    {teamSport === 'Softball' ? '🥎' : '⚾'}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  fontSize: homeNameFontSize,
                  fontWeight: 900,
                  color: '#ffffff',
                  textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                  lineHeight: 1,
                  letterSpacing: 2,
                }}
              >
                {homeTeam || 'YOUR TEAM'}
              </div>
            </div>

            {/* VS */}
            <div
              style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 56,
                fontWeight: 300,
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: 8,
                paddingBottom: 8,
                flexShrink: 0,
              }}
            >
              VS
            </div>

            {/* Away Team */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                flex: 1,
              }}
            >
              <div style={{ ...logoCircleStyle, marginBottom: 16 }}>
                {awayTeamLogoPreview ? (
                  <img
                    src={awayTeamLogoPreview}
                    alt={awayTeam}
                    style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
                  />
                ) : (
                  <span style={{ fontSize: 60 }}>
                    {teamSport === 'Softball' ? '🥎' : '⚾'}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  fontSize: awayNameFontSize,
                  fontWeight: 900,
                  color: '#ffffff',
                  textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                  lineHeight: 1,
                  letterSpacing: 2,
                  textAlign: 'right',
                }}
              >
                {awayTeam || 'OPPONENT'}
              </div>
            </div>
          </div>

          {/* Details Strip */}
          <div
            style={{
              background: 'rgba(0,0,0,0.6)',
              borderTop: `4px solid ${accent}`,
              padding: '32px 60px',
              display: 'flex',
              gap: 48,
              alignItems: 'center',
              marginLeft: -60,
              marginRight: -60,
            }}
          >
            {gameDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                <span style={{ fontFamily: 'Barlow, sans-serif', fontSize: 28, fontWeight: 600, color: detailTextColor || '#ffffff' }}>
                  {formatGameDate(gameDate, dateFormat)}
                </span>
              </div>
            )}
            {gameTime && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                <span style={{ fontFamily: 'Barlow, sans-serif', fontSize: 28, fontWeight: 600, color: detailTextColor || '#ffffff' }}>
                  {formatGameTime(gameTime)}
                </span>
              </div>
            )}
            {location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                <span style={{ fontFamily: 'Barlow, sans-serif', fontSize: 28, fontWeight: 600, color: detailTextColor || '#ffffff' }}>
                  {location}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 3,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicPreviewPhotoFrame;
